import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { getCandleData, getMarketOverview, getPivotPoints, getTechnicalIndicator, getMacdIndicators, TechnicalIndicatorData } from '../api/metrics';
import { getDefaultDateRange } from '../utils/dateRangeDefaults';
import { getPivotFetchRange } from '../utils/pivotDateRanges';
import { TimezoneMode, loadTimezone, saveTimezone } from '../utils/timezone';
import { getDefaultMovingAverages, isMovingAverageValid } from '../utils/chartMovingAverages';
import { isIchimokuAvailable } from '../utils/granularityMetricConfig';
import SectionTitle from './shared/SectionTitle';
import StandaloneSection from './shared/StandaloneSection';
import CandlestickChart from './charts/CandlestickChart';
import IndicatorControls, { MovingAverageConfig } from './charts/IndicatorControls';
import MacdPanel from './charts/MacdPanel';
import RsiPanel from './charts/RsiPanel';
import ObvPanel from './charts/ObvPanel';
import AtrPanel from './charts/AtrPanel';
import AdxPanel from './charts/AdxPanel';
import { ConfluenceZoneCard } from './ConfluenceZoneCard';
import { PivotLevelReliabilityTable } from './PivotLevelReliabilityTable';
import PivotPointVisualizationChart from './PivotPointVisualization/PivotPointVisualizationChart';
import { PivotTimeframe } from '../types/pivotPoints.ts';
import DashboardBottomLogo from './DashboardBottomLogo';
import ProductPicker from './charts/ProductPicker';
import PriceIntelligencePanel from './PriceIntelligencePanel';
import VolatilityDetailPanel from './VolatilityDetailPanel';
import './ChartView.css';

// Timeframe configurations
type TimeframeKey = '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d';

interface TimeframeConfig {
  label: string;
  granularityMins: number;
}

const TIMEFRAMES: Record<TimeframeKey, TimeframeConfig> = {
  '1m': { label: '1 Minute', granularityMins: 1 },
  '5m': { label: '5 Minutes', granularityMins: 5 },
  '15m': { label: '15 Minutes', granularityMins: 15 },
  '30m': { label: '30 Minutes', granularityMins: 30 },
  '1h': { label: '1 Hour', granularityMins: 60 },
  '4h': { label: '4 Hours', granularityMins: 240 },
  '1d': { label: 'Daily', granularityMins: 1440 },
};

// Chart tab constants
const CHART_TAB_DAILY = 'daily' as const;
const CHART_TAB_PIVOTS = 'pivots' as const;
const CHART_TAB_INTELLIGENCE = 'intelligence' as const;
const CHART_TAB_PRICE_INTEL = 'price_intelligence' as const;
const CHART_TAB_VOLATILITY = 'volatility' as const;

const ChartView: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Validate productId exists
  if (!productId) {
    return (
      <div className="chart-view">
        <div className="chart-error">
          <p className="error-title">Invalid Product</p>
          <p className="error-message">No product ID specified in URL</p>
          <Link to="/market-overview" className="back-link">
            ← Back to Market Overview
          </Link>
        </div>
      </div>
    );
  }

  // Chart-level tab state (Price Chart vs Pivot Points vs Pivot Intelligence vs Volatility vs ATR)
  const urlChartTab = searchParams.get('chartTab') || CHART_TAB_DAILY;
  const [chartTab, setChartTab] = useState<'daily' | 'pivots' | 'intelligence' | 'price_intelligence' | 'volatility'>(
    urlChartTab === CHART_TAB_PIVOTS ? CHART_TAB_PIVOTS :
    urlChartTab === CHART_TAB_INTELLIGENCE ? CHART_TAB_INTELLIGENCE :
    urlChartTab === CHART_TAB_PRICE_INTEL ? CHART_TAB_PRICE_INTEL :
    urlChartTab === CHART_TAB_VOLATILITY ? CHART_TAB_VOLATILITY :
    CHART_TAB_DAILY
  );

  // Get timeframe from URL or default to 15m
  const urlTimeframe = (searchParams.get('timeframe') || '15m') as TimeframeKey;
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeKey>(
    TIMEFRAMES[urlTimeframe] ? urlTimeframe : '15m'
  );
  
  const granularity = TIMEFRAMES[selectedTimeframe].granularityMins;


  // Pivot point settings for chart-level pivot tab
  const [pivotTimeframe, setPivotTimeframe] = useState<'TWENTY_FOUR_HOUR' | 'SEVEN_DAY' | 'THIRTY_DAY' | 'THIRTY_MIN' | 'ONE_HOUR' | 'TWO_HOUR' | 'FOUR_HOUR' | 'EIGHT_HOUR' | 'TWELVE_HOUR'>('ONE_HOUR');
  const [pivotType, setPivotType] = useState<'STANDARD' | 'FIBONACCI' | 'WOODIE'>('STANDARD');

  // Pivot Intelligence tab timeframe - synced with localStorage for chart component
  const [intelligenceTimeframe, setIntelligenceTimeframe] = useState<PivotTimeframe>(() => {
    return (localStorage.getItem('pivot-chart-timeframe') as PivotTimeframe) || 'TWENTY_FOUR_HOUR';
  });

  // Sync intelligence timeframe to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pivot-chart-timeframe', intelligenceTimeframe);
  }, [intelligenceTimeframe]);

  // Calculate date range for Pivot Intelligence tab based on timeframe
  // Use extended fetch range - the chart component handles its own initial visible range
  const intelligenceDateRange = useMemo(() => getPivotFetchRange(intelligenceTimeframe), [intelligenceTimeframe]);

  // Indicator state — load from session storage in a single pass.
  // Persisted shape: { movingAverages: MovingAverageConfig[], <indicatorName>Enabled: boolean, ... }
  // The unknown-field-tolerant ?? fallback below keeps backward compatibility
  // when a new indicator's flag isn't yet present in the persisted blob.
  const initialIndicators = ((): Record<string, unknown> => {
    const saved = sessionStorage.getItem('chartIndicators');
    if (!saved) return {};
    try {
      return JSON.parse(saved) ?? {};
    } catch {
      return {};
    }
  })();

  const [movingAverages, setMovingAverages] = useState<MovingAverageConfig[]>(
    (initialIndicators.movingAverages as MovingAverageConfig[] | undefined)
      ?? getDefaultMovingAverages(granularity),
  );
  const [macdEnabled, setMacdEnabled] = useState<boolean>(
    (initialIndicators.macdEnabled as boolean | undefined) ?? false,
  );
  const [rsiEnabled, setRsiEnabled] = useState<boolean>(
    (initialIndicators.rsiEnabled as boolean | undefined) ?? false,
  );
  const [bollingerEnabled, setBollingerEnabled] = useState<boolean>(
    (initialIndicators.bollingerEnabled as boolean | undefined) ?? false,
  );
  const [vwapEnabled, setVwapEnabled] = useState<boolean>(
    (initialIndicators.vwapEnabled as boolean | undefined) ?? false,
  );
  const [volumeSmaEnabled, setVolumeSmaEnabled] = useState<boolean>(
    (initialIndicators.volumeSmaEnabled as boolean | undefined) ?? false,
  );
  const [volumeEmaEnabled, setVolumeEmaEnabled] = useState<boolean>(
    (initialIndicators.volumeEmaEnabled as boolean | undefined) ?? false,
  );
  const [obvEnabled, setObvEnabled] = useState<boolean>(
    (initialIndicators.obvEnabled as boolean | undefined) ?? false,
  );
  const [atrEnabled, setAtrEnabled] = useState<boolean>(
    (initialIndicators.atrEnabled as boolean | undefined) ?? false,
  );
  const [adxEnabled, setAdxEnabled] = useState<boolean>(
    (initialIndicators.adxEnabled as boolean | undefined) ?? false,
  );
  const [ichimokuEnabled, setIchimokuEnabled] = useState<boolean>(
    (initialIndicators.ichimokuEnabled as boolean | undefined) ?? false,
  );

  // Timezone state - shared across all charts
  const [timezone, setTimezone] = useState<TimezoneMode>(loadTimezone());

  // Save timezone when it changes
  useEffect(() => {
    saveTimezone(timezone);
  }, [timezone]);

  // Save all indicator preferences to session storage
  useEffect(() => {
    sessionStorage.setItem('chartIndicators', JSON.stringify({
      movingAverages,
      macdEnabled,
      rsiEnabled,
      bollingerEnabled,
      vwapEnabled,
      volumeSmaEnabled,
      volumeEmaEnabled,
      obvEnabled,
      atrEnabled,
      adxEnabled,
      ichimokuEnabled,
    }));
  }, [movingAverages, macdEnabled, rsiEnabled, bollingerEnabled, vwapEnabled, volumeSmaEnabled, volumeEmaEnabled, obvEnabled, atrEnabled, adxEnabled, ichimokuEnabled]);

  // Reset moving averages when granularity changes to avoid requesting unavailable indicators
  useEffect(() => {
    setMovingAverages(getDefaultMovingAverages(granularity));
  }, [granularity]);

  // Update URL when timeframe or chartTab changes
  useEffect(() => {
    const params: any = { timeframe: selectedTimeframe };
    if (chartTab !== CHART_TAB_DAILY) {
      params.chartTab = chartTab;
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeframe, chartTab]);

  // Calculate default date range for the current granularity
  const dateRange = useMemo(() => getDefaultDateRange(granularity), [granularity]);

  // Fetch candle data with improved caching
  const { data, isLoading, error } = useQuery({
    queryKey: ['candles', productId, granularity, dateRange.startTime, dateRange.endTime],
    queryFn: () => getCandleData(productId, granularity, dateRange.startTime, dateRange.endTime),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes - longer caching
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Fetch market context data with caching
  const { data: marketOverview } = useQuery({
    queryKey: ['marketOverview'],
    queryFn: getMarketOverview,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch technical indicators - ONE REQUEST PER INDICATOR
  // Use useQueries (plural) for dynamic array of queries - this follows Rules of Hooks
  // CRITICAL: Filter to only include MAs that are valid for current granularity to avoid 404s
  const enabledMAs = movingAverages
    .filter(ma => ma.enabled)
    .filter(ma => isMovingAverageValid(granularity, ma.type, ma.period));

  // Fetch each moving average individually using useQueries
  // `combine` is an identity pass-through, but its mere presence opts into
  // TanStack Query's replaceEqualDeep structural-sharing path, so `maQueries`
  // keeps the same array reference across renders when nothing changed
  // (PLAN-AMEND-003) — without it, `indicatorData`'s useMemo below recomputes
  // on every ChartView re-render (e.g. any indicator toggle), which cascades
  // into the CandlestickChart `indicators` prop and defeats the dedicated-
  // effect migration's chart-recreation avoidance.
  const maQueries = useQueries({
    queries: enabledMAs.map(ma => ({
      queryKey: ['technicalIndicator', productId, granularity, ma.type, ma.period, dateRange.startTime, dateRange.endTime],
      queryFn: () => getTechnicalIndicator(productId, granularity, ma.type, ma.period, dateRange.startTime, dateRange.endTime),
      enabled: !!productId && chartTab === CHART_TAB_DAILY,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
    combine: (results) => results,
  });

  // Fetch RSI if enabled
  const { data: rsiData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'RSI', 14, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'RSI', 14, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && rsiEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch Bollinger Bands (three separate requests)
  const { data: bbUpperData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'BOLLINGER_UPPER', 20, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'BOLLINGER_UPPER', 20, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && bollingerEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: bbMiddleData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'BOLLINGER_MIDDLE', 20, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'BOLLINGER_MIDDLE', 20, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && bollingerEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: bbLowerData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'BOLLINGER_LOWER', 20, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'BOLLINGER_LOWER', 20, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && bollingerEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch VWAP (three separate requests)
  const { data: vwapData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'VWAP', 20, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'VWAP', 20, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && vwapEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: vwapUpperData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'VWAP_UPPER', 20, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'VWAP_UPPER', 20, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && vwapEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: vwapLowerData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'VWAP_LOWER', 20, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'VWAP_LOWER', 20, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && vwapEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch Volume SMA if enabled
  const { data: volumeSmaData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'VOLUME_SMA', 20, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'VOLUME_SMA', 20, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && volumeSmaEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch Volume EMA if enabled
  const { data: volumeEmaData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'VOLUME_EMA', 20, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'VOLUME_EMA', 20, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && volumeEmaEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch OBV if enabled
  const { data: obvData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'OBV', -1, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'OBV', -1, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && obvEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch ATR if enabled
  const { data: atrData, isLoading: isAtrLoading } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'ATR', 14, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'ATR', 14, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && atrEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch ADX if enabled
  const { data: adxData, isLoading: isAdxLoading } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'ADX', 14, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'ADX', 14, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && adxEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch Ichimoku components if enabled — five separate queries (one per component).
  // Gated on isIchimokuAvailable so we never request at granularities the backend doesn't compute.
  const ichimokuActive = chartTab === CHART_TAB_DAILY && ichimokuEnabled && isIchimokuAvailable(granularity);

  const { data: tenkanData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'TENKAN_SEN', 9, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'TENKAN_SEN', 9, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && ichimokuActive,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: kijunData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'KIJUN_SEN', 26, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'KIJUN_SEN', 26, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && ichimokuActive,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: senkouAData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'SENKOU_SPAN_A', 26, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'SENKOU_SPAN_A', 26, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && ichimokuActive,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: senkouBData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'SENKOU_SPAN_B', 52, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'SENKOU_SPAN_B', 52, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && ichimokuActive,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: chikouData } = useQuery({
    queryKey: ['technicalIndicator', productId, granularity, 'CHIKOU_SPAN', 26, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularity, 'CHIKOU_SPAN', 26, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && ichimokuActive,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Combine all indicator data into a single array for the chart
  const indicatorData = useMemo(() => {
    const allIndicators: TechnicalIndicatorData[] = [];

    // Add moving average data
    maQueries.forEach(query => {
      if (query.data?.indicators) {
        allIndicators.push(...query.data.indicators);
      }
    });

    // Add RSI data
    if (rsiData?.indicators) {
      allIndicators.push(...rsiData.indicators);
    }

    // Add Bollinger Bands data
    if (bbUpperData?.indicators) {
      allIndicators.push(...bbUpperData.indicators);
    }
    if (bbMiddleData?.indicators) {
      allIndicators.push(...bbMiddleData.indicators);
    }
    if (bbLowerData?.indicators) {
      allIndicators.push(...bbLowerData.indicators);
    }

    // Add VWAP data
    if (vwapData?.indicators) {
      allIndicators.push(...vwapData.indicators);
    }
    if (vwapUpperData?.indicators) {
      allIndicators.push(...vwapUpperData.indicators);
    }
    if (vwapLowerData?.indicators) {
      allIndicators.push(...vwapLowerData.indicators);
    }

    // Add Volume indicators
    if (volumeSmaData?.indicators) {
      allIndicators.push(...volumeSmaData.indicators);
    }
    if (volumeEmaData?.indicators) {
      allIndicators.push(...volumeEmaData.indicators);
    }

    // Add OBV data
    if (obvData?.indicators) {
      allIndicators.push(...obvData.indicators);
    }

    // Add ATR data
    if (atrData?.indicators) {
      allIndicators.push(...atrData.indicators);
    }

    // Add Ichimoku component data (REQ-003: all five together or none)
    if (tenkanData?.indicators) {
      allIndicators.push(...tenkanData.indicators);
    }
    if (kijunData?.indicators) {
      allIndicators.push(...kijunData.indicators);
    }
    if (senkouAData?.indicators) {
      allIndicators.push(...senkouAData.indicators);
    }
    if (senkouBData?.indicators) {
      allIndicators.push(...senkouBData.indicators);
    }
    if (chikouData?.indicators) {
      allIndicators.push(...chikouData.indicators);
    }

    return allIndicators;
  }, [maQueries, rsiData, bbUpperData, bbMiddleData, bbLowerData, vwapData, vwapUpperData, vwapLowerData, volumeSmaData, volumeEmaData, obvData, atrData, tenkanData, kijunData, senkouAData, senkouBData, chikouData]);

  // Fetch MACD indicators when enabled
  // Note: MACD requires minimum 5-minute granularity (1-minute not supported by backend)
  const isMacdSupported = granularity >= 5;
  const { data: macdData, isLoading: isMacdLoading } = useQuery({
    queryKey: ['macdIndicators', productId, granularity, dateRange.startTime, dateRange.endTime],
    queryFn: () => getMacdIndicators(productId, granularity, 12, 26, 9, dateRange.startTime, dateRange.endTime),
    enabled: !!productId && chartTab === CHART_TAB_DAILY && macdEnabled && isMacdSupported,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch pivot points data for chart-level pivot tab
  const { data: pivotData, isLoading: isPivotLoading, isError: isPivotError, error: pivotError } = useQuery({
    queryKey: ['pivotPoints', productId, pivotTimeframe, pivotType],
    queryFn: () => getPivotPoints(productId, pivotTimeframe, pivotType),
    enabled: !!productId && chartTab === CHART_TAB_PIVOTS,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Get market context for current product (memoized)
  const marketContext = useMemo(() => {
    return marketOverview?.markets?.find(
      (m: any) => m.productId === productId
    );
  }, [marketOverview, productId]);

  // Handle timeframe change
  const handleTimeframeChange = (timeframe: TimeframeKey) => {
    setSelectedTimeframe(timeframe);
  };

  // Format product name for display
  const productName = productId?.replace('-', ' / ') || '';

  // Set page title
  useEffect(() => {
    if (productId) {
      document.title = `${productId} Chart - Cooter Capital`;
    }
    return () => {
      document.title = 'Cooter Capital';
    };
  }, [productId]);

  // Get current price and change from candle data (memoized)
  const priceMetrics = useMemo(() => {
    if (!data?.candles || data.candles.length === 0) {
      return { currentPrice: 0, priceChange: 0, priceChangePercent: 0 };
    }

    const currentPrice = Number(data.candles[data.candles.length - 1].close);
    const startPrice = data.candles.length > 1 
      ? Number(data.candles[0].close) 
      : currentPrice;
    const priceChange = currentPrice - startPrice;
    const priceChangePercent = startPrice > 0 ? ((priceChange / startPrice) * 100) : 0;

    return { currentPrice, priceChange, priceChangePercent };
  }, [data]);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(value);
  };

  // Format price change
  const formatPriceChange = (change: number, percent: number) => {
    const sign = change >= 0 ? '+' : '';
    const className = change >= 0 ? 'positive' : 'negative';
    return (
      <span className={`price-change ${className}`}>
        {sign}{formatCurrency(change)} ({sign}{percent.toFixed(2)}%)
      </span>
    );
  };

  // Get trend icon
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'UPTREND':
        return <span className="trend-icon uptrend" title="Uptrend">↑</span>;
      case 'DOWNTREND':
        return <span className="trend-icon downtrend" title="Downtrend">↓</span>;
      case 'SIDEWAYS':
        return <span className="trend-icon sideways" title="Sideways">→</span>;
      default:
        return null;
    }
  };

  // Get volatility badge
  const getVolatilityBadge = (state: string) => {
    if (!state || state === 'UNKNOWN') return null;
    const className = `volatility-badge volatility-${state.toLowerCase()}`;
    return <span className={className}>{state}</span>;
  };

  // Pivot point helper functions
  const formatCurrencyPivot = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';
    const decimalPlaces = Math.abs(value) < 1 ? 4 : 2;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  };

  const formatPercentage = (current: number, level: number | null): string => {
    if (level === null) return 'N/A';
    const diff = ((current - level) / level) * 100;
    const sign = diff >= 0 ? '+' : '';
    return `${sign}${diff.toFixed(2)}%`;
  };

  const getStatusColor = (status: string): string => {
    if (status.includes('Above')) return 'status-bullish';
    if (status.includes('Below')) return 'status-bearish';
    return 'status-neutral';
  };

  const calculateDistance = (currentPrice: number, level: number | null): { amount: string; percent: string } => {
    if (level === null) return { amount: 'N/A', percent: 'N/A' };
    const distance = level - currentPrice;
    const percent = (distance / currentPrice) * 100;
    const sign = distance >= 0 ? '+' : '';
    return {
      amount: `${sign}${formatCurrency(Math.abs(distance))}`,
      percent: `${sign}${percent.toFixed(2)}%`,
    };
  };

  // Generate AI-powered trade ladder recommendations
  const generateTradeLadderRecommendations = (pivotData: any, productName: string) => {
    const current = pivotData.currentPrice;
    const pp = pivotData.pivotPoint;
    const r1 = pivotData.resistance1;
    const r2 = pivotData.resistance2;
    const r3 = pivotData.resistance3;
    const s1 = pivotData.support1;
    const s2 = pivotData.support2;
    const s3 = pivotData.support3;

    // Determine market position
    const abovePivot = current > pp;
    const nearestResistance = pivotData.nearestResistance;
    const nearestSupport = pivotData.nearestSupport;

    // Calculate distances
    const distanceToResistance = nearestResistance ? ((nearestResistance - current) / current) * 100 : 0;
    const distanceToSupport = nearestSupport ? ((current - nearestSupport) / current) * 100 : 0;

    // Determine strategy
    let strategy: string;
    let buyLadder: Array<{ level: string; price: number | null; allocation: string; reasoning: string }>;
    let sellLadder: Array<{ level: string; price: number | null; allocation: string; reasoning: string }>;
    let riskAssessment: string;

    if (abovePivot) {
      // Price above pivot - bullish bias
      strategy = 'bullish';
      riskAssessment = distanceToResistance < 2
        ? '⚠️ HIGH RISK - Price near resistance. Consider waiting for breakout or pullback.'
        : distanceToResistance < 5
        ? '⚡ MODERATE RISK - Approaching resistance. Scale entries carefully.'
        : '✅ FAVORABLE - Good distance from resistance for continuation.';

      buyLadder = [
        { level: 'Pivot Point', price: pp, allocation: '30%', reasoning: 'Primary support level, high probability bounce zone' },
        { level: 'S1', price: s1, allocation: '25%', reasoning: 'First support below pivot, strong technical level' },
        { level: 'S2', price: s2, allocation: '20%', reasoning: 'Secondary support, for deeper pullbacks' },
        { level: 'S3', price: s3, allocation: '15%', reasoning: 'Deep support, oversold conditions likely' },
        { level: 'Current -2%', price: current * 0.98, allocation: '10%', reasoning: 'Conservative entry near current price' },
      ].filter(l => l.price !== null);

      sellLadder = [
        { level: 'R1', price: r1, allocation: '25%', reasoning: 'First resistance target, natural profit-taking zone' },
        { level: 'R2', price: r2, allocation: '35%', reasoning: 'Strong resistance, major profit target' },
        { level: 'R3', price: r3, allocation: '30%', reasoning: 'Extended target, breakout continuation' },
        { level: 'Current +3%', price: current * 1.03, allocation: '10%', reasoning: 'Quick profit on immediate moves' },
      ].filter(l => l.price !== null);

    } else {
      // Price below pivot - bearish bias
      strategy = 'defensive';
      riskAssessment = distanceToSupport < 2
        ? '� CRITICAL - Price near support. Risk of breakdown. Tight stops recommended.'
        : distanceToSupport < 5
        ? '⚠️ CAUTION - Approaching support. Watch for bounce or breakdown.'
        : '� NEUTRAL - Price has room to move. Look for reversal signals.';

      buyLadder = [
        { level: 'S1', price: s1, allocation: '25%', reasoning: 'First support, potential bounce zone' },
        { level: 'S2', price: s2, allocation: '35%', reasoning: 'Strong support level, high R/R entry' },
        { level: 'S3', price: s3, allocation: '30%', reasoning: 'Deep value zone, capitulation entry' },
        { level: 'Current -3%', price: current * 0.97, allocation: '10%', reasoning: 'Conservative dip-buying' },
      ].filter(l => l.price !== null);

      sellLadder = [
        { level: 'Pivot Point', price: pp, allocation: '30%', reasoning: 'Major resistance, likely rejection zone' },
        { level: 'R1', price: r1, allocation: '35%', reasoning: 'Strong overhead resistance' },
        { level: 'R2', price: r2, allocation: '25%', reasoning: 'Extended resistance target' },
        { level: 'Current +5%', price: current * 1.05, allocation: '10%', reasoning: 'Take profit on bounces' },
      ].filter(l => l.price !== null);
    }

    return (
      <div className="recommendation-details">
        {/* Market Assessment */}
        <div className="market-assessment">
          <div className="assessment-header">
            <h4>Market Position Analysis</h4>
            <span className={`strategy-badge ${strategy}`}>{strategy.toUpperCase()} BIAS</span>
          </div>
          <p className="assessment-text">
            {productName} is currently trading <strong>{abovePivot ? 'ABOVE' : 'BELOW'}</strong> the {pivotData.timeframe.toLowerCase()} pivot point
            at <strong>{formatCurrencyPivot(pp)}</strong>, indicating a {abovePivot ? 'bullish' : 'bearish'} short-term bias.
            {nearestResistance && ` The nearest resistance at ${formatCurrencyPivot(nearestResistance)} is ${Math.abs(distanceToResistance).toFixed(2)}% away.`}
            {nearestSupport && ` The nearest support at ${formatCurrencyPivot(nearestSupport)} is ${Math.abs(distanceToSupport).toFixed(2)}% away.`}
          </p>
          <div className={`risk-badge ${distanceToResistance < 2 || distanceToSupport < 2 ? 'high' : distanceToResistance < 5 || distanceToSupport < 5 ? 'medium' : 'low'}`}>
            {riskAssessment}
          </div>
        </div>

        {/* Buy Ladder Strategy */}
        <div className="ladder-section">
          <h4 className="ladder-title">� Recommended BUY Ladder</h4>
          <p className="ladder-description">
            Scale into positions at key support levels to average a favorable entry price while managing risk.
          </p>
          <table className="ladder-table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Target Price</th>
                <th>Allocation</th>
                <th>Reasoning</th>
              </tr>
            </thead>
            <tbody>
              {buyLadder.map((rung, idx) => (
                <tr key={idx} className={rung.price && Math.abs(rung.price - current) / current < 0.01 ? 'near-current' : ''}>
                  <td className="ladder-level">{rung.level}</td>
                  <td className="ladder-price">{formatCurrencyPivot(rung.price)}</td>
                  <td className="ladder-allocation">{rung.allocation}</td>
                  <td className="ladder-reasoning">{rung.reasoning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Sell Ladder Strategy */}
        <div className="ladder-section">
          <h4 className="ladder-title">� Recommended SELL Ladder</h4>
          <p className="ladder-description">
            Take profits at resistance levels to lock in gains while leaving room for further upside.
          </p>
          <table className="ladder-table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Target Price</th>
                <th>Allocation</th>
                <th>Reasoning</th>
              </tr>
            </thead>
            <tbody>
              {sellLadder.map((rung, idx) => (
                <tr key={idx} className={rung.price && Math.abs(rung.price - current) / current < 0.01 ? 'near-current' : ''}>
                  <td className="ladder-level">{rung.level}</td>
                  <td className="ladder-price">{formatCurrencyPivot(rung.price)}</td>
                  <td className="ladder-allocation">{rung.allocation}</td>
                  <td className="ladder-reasoning">{rung.reasoning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Key Insights */}
        <div className="key-insights">
          <h4>� Key Trading Insights</h4>
          <ul>
            <li>
              <strong>Stop Loss Placement:</strong> Set stops below {s1 ? formatCurrencyPivot(s1) : 'nearest support'} for long positions,
              above {r1 ? formatCurrencyPivot(r1) : 'nearest resistance'} for short positions.
            </li>
            <li>
              <strong>Breakout Strategy:</strong> A decisive break {abovePivot ? `above ${formatCurrencyPivot(r1)}` : `below ${formatCurrencyPivot(s1)}`}
              {' '}could signal a continuation move to the next level.
            </li>
            <li>
              <strong>Risk Management:</strong> Never allocate more than 1-2% of portfolio per entry.
              Use the ladder to scale in with smaller positions at each level.
            </li>
            <li>
              <strong>Timeframe Context:</strong> These {pivotData.timeframe.toLowerCase()} pivots are recalculated each period.
              Monitor intraday price action for optimal entry/exit timing.
            </li>
            {pivotData.type !== 'STANDARD' && (
              <li>
                <strong>{pivotData.type} Pivots:</strong> {pivotData.type === 'FIBONACCI'
                  ? 'Uses Fibonacci ratios (0.382, 0.618) for more aggressive support/resistance levels.'
                  : "Woodie's pivots weight the current period's open price more heavily, useful for trending markets."}
              </li>
            )}
          </ul>
        </div>

        {/* Disclaimer */}
        <div className="recommendation-disclaimer">
          <p>
            ⚠️ <strong>Disclaimer:</strong> These recommendations are based on technical analysis of pivot points and should not be considered financial advice.
            Always conduct your own research, consider your risk tolerance, and never invest more than you can afford to lose.
            Market conditions can change rapidly, making past patterns unreliable indicators of future performance.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="chart-view">
      {/* Page Header with Product Picker */}
      <div className="chart-view-header">
        <SectionTitle
          title="Price Charts"
        />
        {marketOverview && marketOverview.markets.length > 0 && (
          <ProductPicker
            currentProductId={productId}
            products={marketOverview.markets}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="chart-navigation">
        <Link to="/market-overview" className="back-link">
          ← Back to Market Overview
        </Link>
      </div>

      {/* Chart Section */}
      <StandaloneSection
        title={`${productName} CHART`}
        className="chart-section"
        style={{
          minHeight: chartTab === CHART_TAB_DAILY
            ? `${1200 +
                (macdEnabled && isMacdSupported ? 200 : 0) +
                (rsiEnabled ? 320 : 0) +
                (obvEnabled ? 320 : 0) +
                (atrEnabled ? 320 : 0) +
                (adxEnabled ? 320 : 0)}px`
            : undefined
        }}
      >
        {/* Chart-level tabs - styled like Profit Monitor tabs */}
        <div className="chart-tabs">
          <button
            className={`chart-tab-button ${chartTab === CHART_TAB_DAILY ? 'active' : ''}`}
            onClick={() => setChartTab(CHART_TAB_DAILY)}
          >
            Price Chart
          </button>
            <button
                className={`chart-tab-button ${chartTab === CHART_TAB_PRICE_INTEL ? 'active' : ''}`}
                onClick={() => setChartTab(CHART_TAB_PRICE_INTEL)}
            >
                Price Intelligence
            </button>
          <button
            className={`chart-tab-button ${chartTab === CHART_TAB_PIVOTS ? 'active' : ''}`}
            onClick={() => setChartTab(CHART_TAB_PIVOTS)}
          >
            Pivot Points (Experimental)
          </button>
          <button
            className={`chart-tab-button ${chartTab === CHART_TAB_INTELLIGENCE ? 'active' : ''}`}
            onClick={() => setChartTab(CHART_TAB_INTELLIGENCE)}
          >
            Pivot Intelligence (Experimental)
          </button>
          <button
            className={`chart-tab-button ${chartTab === CHART_TAB_VOLATILITY ? 'active' : ''}`}
            onClick={() => setChartTab(CHART_TAB_VOLATILITY)}
          >
            Volatility
          </button>
        </div>

        {/* Daily Chart Tab Content */}
        {chartTab === CHART_TAB_DAILY && (
          <>
            <div className="chart-header">
          <div className="chart-info">
            <div className="product-name">{productName}</div>
            {data && (
              <div className="price-info">
                <span className="current-price">{formatCurrency(priceMetrics.currentPrice)}</span>
                <span className="price-change-range">{formatPriceChange(priceMetrics.priceChange, priceMetrics.priceChangePercent)}</span>
              </div>
            )}
            {/* Market Context Badges */}
            {marketContext && (
              <div className="market-context-badges">
                {getTrendIcon(marketContext.trend)}
                {getVolatilityBadge(marketContext.volatilityState)}
              </div>
            )}
          </div>
          <div className="chart-timeframe">
            <div className="timeframe-selector">
              <label className="granularity-label">Granularity:</label>
              {(Object.keys(TIMEFRAMES) as TimeframeKey[]).map((tf) => (
                <button
                  key={tf}
                  className={`timeframe-button ${selectedTimeframe === tf ? 'active' : ''}`}
                  onClick={() => handleTimeframeChange(tf)}
                  disabled={isLoading}
                >
                  {tf.toUpperCase()}
                </button>
              ))}
            </div>
            {/* Indicator Controls - Compact Dropdown */}
            <IndicatorControls
              movingAverages={movingAverages}
              onMovingAveragesChange={setMovingAverages}
              macdEnabled={macdEnabled}
              onMacdToggle={setMacdEnabled}
              rsiEnabled={rsiEnabled}
              onRsiToggle={setRsiEnabled}
              bollingerEnabled={bollingerEnabled}
              onBollingerToggle={setBollingerEnabled}
              vwapEnabled={vwapEnabled}
              onVwapToggle={setVwapEnabled}
              volumeSmaEnabled={volumeSmaEnabled}
              onVolumeSmaToggle={setVolumeSmaEnabled}
              volumeEmaEnabled={volumeEmaEnabled}
              onVolumeEmaToggle={setVolumeEmaEnabled}
              obvEnabled={obvEnabled}
              onObvToggle={setObvEnabled}
              atrEnabled={atrEnabled}
              onAtrToggle={setAtrEnabled}
              adxEnabled={adxEnabled}
              onAdxToggle={setAdxEnabled}
              ichimokuEnabled={ichimokuEnabled}
              onIchimokuToggle={setIchimokuEnabled}
              granularityMins={granularity}
            />
          </div>
        </div>

        <div className="chart-container">
          {isLoading && (
            <div className="chart-loading">
              <div className="loading-spinner"></div>
              <p>Loading chart data...</p>
            </div>
          )}

          {error && (
            <div className="chart-error">
              <p className="error-title">Failed to load chart data</p>
              <p className="error-message">{error instanceof Error ? error.message : 'Unknown error occurred'}</p>
              <button 
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          )}

          {data && !isLoading && !error && (
            <>
              <CandlestickChart
                data={data.candles}
                loading={isLoading}
                granularityMins={granularity}
                indicators={indicatorData || []}
                movingAverages={movingAverages}
                bollingerEnabled={bollingerEnabled}
                vwapEnabled={vwapEnabled}
                rsiEnabled={rsiEnabled}
                volumeSmaEnabled={volumeSmaEnabled}
                volumeEmaEnabled={volumeEmaEnabled}
                ichimokuEnabled={ichimokuEnabled}
                productId={productId}
                timezone={timezone}
                onTimezoneChange={setTimezone}
              />

              {/* MACD Panel */}
              {macdEnabled && isMacdSupported && macdData && (
                <div style={{ marginTop: '16px', height: '180px' }}>
                  <MacdPanel
                    data={macdData.macdData || []}
                    loading={isMacdLoading}
                    timezone={timezone}
                    granularityMins={granularity}
                  />
                </div>
              )}

              {/* RSI Panel */}
              {rsiEnabled && rsiData && (
                <div style={{ marginTop: '16px', height: '300px' }}>
                  <RsiPanel
                    data={rsiData.indicators || []}
                    loading={false}
                    timezone={timezone}
                    granularityMins={granularity}
                  />
                </div>
              )}

              {/* OBV Panel */}
              {obvEnabled && obvData && (
                <div style={{ marginTop: '16px', height: '300px' }}>
                  <ObvPanel
                    data={obvData.indicators || []}
                    loading={false}
                    timezone={timezone}
                    granularityMins={granularity}
                  />
                </div>
              )}

              {/* ATR Panel */}
              {atrEnabled && atrData && (
                <div style={{ marginTop: '16px', height: '300px' }}>
                  <AtrPanel
                    data={atrData.indicators || []}
                    loading={isAtrLoading}
                    timezone={timezone}
                    granularityMins={granularity}
                  />
                </div>
              )}

              {/* ADX Panel */}
              {adxEnabled && adxData && (
                <div style={{ marginTop: '16px', height: '300px' }}>
                  <AdxPanel
                    data={adxData.indicators || []}
                    loading={isAdxLoading}
                    timezone={timezone}
                    granularityMins={granularity}
                  />
                </div>
              )}
              
              <div className="chart-metadata">
                <span className="metadata-item">
                  Candles: {data.candleCount}
                </span>
                {data.hasGaps && (
                  <span className="metadata-item warning">
                    ⚠ Data gaps detected
                  </span>
                )}
                <span className="metadata-item">
                  Updated: {new Date(data.timestamp).toLocaleString()}
                </span>
              </div>
            </>
          )}
        </div>
          </>
        )}

        {/* Pivot Points Tab Content */}
        {chartTab === CHART_TAB_PIVOTS && (
          <>
            {/* Pivot Controls */}
            <div className="pivot-controls">
              <div className="control-group">
                <label>Timeframe:</label>
                <select value={pivotTimeframe} onChange={(e) => setPivotTimeframe(e.target.value as any)}>
                    <option value="THIRTY_MIN">30 Minutes</option>
                    <option value="ONE_HOUR">1 Hour</option>
                    <option value="TWO_HOUR">2 Hours</option>
                    <option value="FOUR_HOUR">4 Hours</option>
                  <option value="EIGHT_HOUR">8 Hours</option>
                  <option value="TWELVE_HOUR">12 Hours</option>
                  <option value="TWENTY_FOUR_HOUR">Daily</option>
                  <option value="SEVEN_DAY">Weekly</option>
                  <option value="THIRTY_DAY">Monthly</option>
                </select>
              </div>
              <div className="control-group">
                <label>Pivot Type:</label>
                <select value={pivotType} onChange={(e) => setPivotType(e.target.value as any)}>
                  <option value="STANDARD">Standard</option>
                  <option value="FIBONACCI">Fibonacci</option>
                  <option value="WOODIE">Woodie's</option>
                </select>
              </div>
            </div>

            {isPivotLoading && (
              <div className="chart-loading">
                <div className="loading-spinner"></div>
                <p>Loading pivot point analysis...</p>
              </div>
            )}

            {isPivotError && (
              <div className="chart-error">
                <p className="error-title">Failed to load pivot point data</p>
                <p className="error-message">{pivotError instanceof Error ? pivotError.message : 'Unknown error occurred'}</p>
              </div>
            )}

            {pivotData && !isPivotLoading && !isPivotError && (
              <>
                {/* Hero Metrics Section - Spatially organized left-to-right (low to high) */}
                <div className="pivot-hero-metrics">
                  {/* Always leftmost - Support (lowest price) */}
                  <div className="hero-metric-card support-card">
                    <div className="metric-label">Nearest Support</div>
                    <div className="metric-value">{formatCurrencyPivot(pivotData.nearestSupport)}</div>
                    <div className="metric-detail">
                      {pivotData.nearestSupport ? formatPercentage(pivotData.currentPrice, pivotData.nearestSupport) + ' below' : 'N/A'}
                    </div>
                  </div>

                  {/* Middle two: dynamically ordered based on price position */}
                  {pivotData.currentPrice > pivotData.pivotPoint ? (
                    <>
                      {/* Price above pivot: show Pivot first, then Price */}
                      <div className="hero-metric-card">
                        <div className="metric-label">Pivot Point</div>
                        <div className="metric-value">{formatCurrencyPivot(pivotData.pivotPoint)}</div>
                        <div className="metric-detail">
                          {formatPercentage(pivotData.currentPrice, pivotData.pivotPoint)} from current
                        </div>
                      </div>

                      <div className="hero-metric-card status-card">
                        <div className="metric-label">Current Price</div>
                        <div className={`metric-value large ${getStatusColor(pivotData.status)}`}>
                          {formatCurrencyPivot(pivotData.currentPrice)}
                        </div>
                        <div className="metric-detail">
                          {pivotData.status}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Price below pivot: show Price first, then Pivot */}
                      <div className="hero-metric-card status-card">
                        <div className="metric-label">Current Price</div>
                        <div className={`metric-value large ${getStatusColor(pivotData.status)}`}>
                          {formatCurrencyPivot(pivotData.currentPrice)}
                        </div>
                        <div className="metric-detail">
                          {pivotData.status}
                        </div>
                      </div>

                      <div className="hero-metric-card">
                        <div className="metric-label">Pivot Point</div>
                        <div className="metric-value">{formatCurrencyPivot(pivotData.pivotPoint)}</div>
                        <div className="metric-detail">
                          {formatPercentage(pivotData.currentPrice, pivotData.pivotPoint)} from current
                        </div>
                      </div>
                    </>
                  )}

                  {/* Always rightmost - Resistance (highest price) */}
                  <div className="hero-metric-card resistance-card">
                    <div className="metric-label">Nearest Resistance</div>
                    <div className="metric-value">{formatCurrencyPivot(pivotData.nearestResistance)}</div>
                    <div className="metric-detail">
                      {pivotData.nearestResistance ? formatPercentage(pivotData.currentPrice, pivotData.nearestResistance) + ' above' : 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Detailed Pivot Points Table */}
                <div className="pivot-table-section">
                  <h3 className="section-subtitle">Detailed Pivot Levels</h3>
                  <table className="pivot-points-table">
                    <thead>
                      <tr>
                        <th>Level</th>
                        <th>Price</th>
                        <th>Distance</th>
                        <th>% From Current</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { name: 'R3', price: pivotData.resistance3, type: 'resistance' },
                        { name: 'R2', price: pivotData.resistance2, type: 'resistance' },
                        { name: 'R1', price: pivotData.resistance1, type: 'resistance' },
                        { name: 'PP', price: pivotData.pivotPoint, type: 'pivot' },
                        { name: 'S1', price: pivotData.support1, type: 'support' },
                        { name: 'S2', price: pivotData.support2, type: 'support' },
                        { name: 'S3', price: pivotData.support3, type: 'support' },
                      ].map((level) => {
                        if (level.price === null && level.name !== 'PP') return null;
                        const distance = calculateDistance(pivotData.currentPrice, level.price);
                        const nearest = level.price === pivotData.nearestResistance || level.price === pivotData.nearestSupport;

                        return (
                          <tr key={level.name} className={`level-row ${level.type} ${nearest ? 'nearest' : ''}`}>
                            <td className="level-name">
                              <span className={`level-badge ${level.type}`}>{level.name}</span>
                            </td>
                            <td className="level-price">{formatCurrencyPivot(level.price)}</td>
                            <td className="level-distance">{distance.amount}</td>
                            <td className="level-percent">{distance.percent}</td>
                            <td className="level-status">
                              {nearest && <span className="nearest-badge">← Nearest</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>


                {/* AI-Powered Trade Ladder Recommendations */}
                <div className="trade-recommendations">
                  <h3 className="section-subtitle">Trade Ladder Strategy Recommendations</h3>
                  <div className="recommendation-content">
                    {generateTradeLadderRecommendations(pivotData, productName)}
                  </div>
                </div>

                {/* Calculation Details */}
                <div className="calculation-details">
                  <h4>Previous Period OHLC (used for calculation):</h4>
                  <div className="ohlc-grid">
                    <div className="ohlc-item">
                      <span className="ohlc-label">High:</span>
                      <span className="ohlc-value">{formatCurrencyPivot(pivotData.prevHigh)}</span>
                    </div>
                    <div className="ohlc-item">
                      <span className="ohlc-label">Low:</span>
                      <span className="ohlc-value">{formatCurrencyPivot(pivotData.prevLow)}</span>
                    </div>
                    <div className="ohlc-item">
                      <span className="ohlc-label">Close:</span>
                      <span className="ohlc-value">{formatCurrencyPivot(pivotData.prevClose)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Pivot Intelligence Tab Content */}
        {chartTab === CHART_TAB_INTELLIGENCE && (
          <>
            {/* Pivot Controls */}
            <div className="pivot-controls">
              <div className="control-group">
                <label>Timeframe:</label>
                <select value={intelligenceTimeframe} onChange={(e) => setIntelligenceTimeframe(e.target.value as PivotTimeframe)}>
                  <option value="THIRTY_MIN">30 Minutes</option>
                  <option value="ONE_HOUR">1 Hour</option>
                  <option value="TWO_HOUR">2 Hours</option>
                  <option value="FOUR_HOUR">4 Hours</option>
                  <option value="EIGHT_HOUR">8 Hours</option>
                  <option value="TWELVE_HOUR">12 Hours</option>
                  <option value="TWENTY_FOUR_HOUR">Daily</option>
                  <option value="SEVEN_DAY">Weekly</option>
                  <option value="THIRTY_DAY">Monthly</option>
                </select>
              </div>
            </div>

            <div className="pivot-intelligence-content">
              {/* Pivot Point Visualization Chart */}
              <div className="pivot-visualization-section">
                <PivotPointVisualizationChart
                  productId={productId}
                  timeframe={intelligenceTimeframe}
                />
              </div>

              <div className="confluence-section">
                <ConfluenceZoneCard
                  productId={productId}
                  startDate={intelligenceDateRange.start}
                  endDate={intelligenceDateRange.end}
                  pivotTimeframe={intelligenceTimeframe}
                />
              </div>

              <div className="reliability-section">
                <PivotLevelReliabilityTable
                  productId={productId}
                  lookbackDays={30}
                  selectedTimeframe={intelligenceTimeframe}
                />
              </div>
            </div>
          </>
        )}

        {/* Price Intelligence Tab Content */}
        {chartTab === CHART_TAB_PRICE_INTEL && (
          <PriceIntelligencePanel productId={productId} />
        )}

        {/* ATR Analysis Tab Content */}
        {chartTab === CHART_TAB_VOLATILITY && (
          <VolatilityDetailPanel productId={productId} />
        )}
      </StandaloneSection>

      <DashboardBottomLogo />
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(ChartView);
